/* Vendor-agnostic, CookieYes-first consent adapter. */

import { tryDecodeURIComponent } from "@/lib/url";
import { readDocumentCookies } from "@/lib/browserCookies";

export type ConsentState = {
  initialized: boolean;
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  advertisement: boolean;
};

const createUninitializedConsentState = (): ConsentState => ({
  initialized: false,
  necessary: true,
  functional: false,
  analytics: false,
  advertisement: false,
});

// CookieYes event detail structures (official documented events)
type CookieYesBannerLoadDetail = {
  activeLaw: string;
  categories: {
    necessary: boolean;
    functional: boolean;
    analytics: boolean;
    performance: boolean;
    advertisement: boolean;
  };
  isUserActionCompleted: boolean;
  consentID: string;
  languageCode: string;
};

type CookieYesConsentUpdateDetail = {
  accepted: string[];
  rejected: string[];
};

type Listener = (state: ConsentState) => void;

const listeners = new Set<Listener>();
let handlersInstalled = false;

const COOKIEYES_STATE_COOKIE_NAMES = new Set([
  "cky-consent",
  "cookieyes-consent",
  "cky-functional",
  "cky-performance",
  "cky-preferences",
  "cky-analytics",
  "cky-advertisement",
  "cky-marketing",
  "cookieyes-functional",
  "cookieyes-performance",
  "cookieyes-preferences",
  "cookieyes-analytics",
  "cookieyes-advertisement",
  "cookieyes-marketing",
]);

// Store latest event data from CookieYes events (primary data source)
let latestEventState: ConsentState | null = null;
let latestEventCookieFingerprint: string | undefined;

// Resolve flag lazily so tests can stub env per-case via vi.stubEnv().
function isCookiebarEnabled(): boolean {
  const raw = import.meta.env.VITE_COOKIEBAR_ENABLED;
  return raw === "true" || raw === "1" || raw === true;
}

export function getConsentState(): ConsentState {
  if (!isCookiebarEnabled()) {
    return {
      initialized: true,
      necessary: true,
      functional: true,
      analytics: true,
      advertisement: true,
    };
  }

  const sharedSnapshot = readSharedCookieYesSnapshot();

  // CookieYes events give immediate same-tab updates, including the short interval where the
  // vendor may not have written its cookie yet. Once the relevant shared-cookie fingerprint
  // changes, that snapshot is authoritative so a different tab's withdrawal cannot be masked by
  // a stale tab-local grant.
  if (latestEventState) {
    if (sharedSnapshot && sharedSnapshot.fingerprint !== latestEventCookieFingerprint) {
      latestEventState = sharedSnapshot.state ?? createUninitializedConsentState();
      latestEventCookieFingerprint = sharedSnapshot.fingerprint;
    }
    return latestEventState;
  }

  if (sharedSnapshot?.state) return sharedSnapshot.state;

  // Cookie bar enabled but consent not set yet
  return createUninitializedConsentState();
}

/**
 * Reconciles tab-local event state against the CookieYes cookies shared by every tab. Identity
 * boundaries and focus/visibility changes call this before forwarding consent for a journey.
 */
export function reconcileConsentFromSharedCookie(): ConsentState {
  const sharedSnapshot = readSharedCookieYesSnapshot();
  const next = isCookiebarEnabled() ? (sharedSnapshot?.state ?? createUninitializedConsentState()) : getConsentState();
  latestEventState = next;
  latestEventCookieFingerprint = sharedSnapshot?.fingerprint;
  // Subscribers own value dedupe. Re-observing unchanged consent lets the sender recover after
  // the API exhausted its dropped-acknowledgement retry budget.
  notifyAll();
  return next;
}

// useSyncExternalStore requires getSnapshot to return the same reference until
// the store content actually changes; getConsentState builds fresh objects on
// the cookie/default paths, so cache by content here.
let cachedSnapshot: ConsentState | null = null;

function consentStatesEqual(a: ConsentState, b: ConsentState): boolean {
  return (
    a.initialized === b.initialized &&
    a.necessary === b.necessary &&
    a.functional === b.functional &&
    a.analytics === b.analytics &&
    a.advertisement === b.advertisement
  );
}

export function getConsentSnapshot(): ConsentState {
  const next = getConsentState();
  if (cachedSnapshot === null || !consentStatesEqual(cachedSnapshot, next)) {
    cachedSnapshot = next;
  }
  return cachedSnapshot;
}

const disabledServerSnapshot: ConsentState = {
  initialized: true,
  necessary: true,
  functional: true,
  analytics: true,
  advertisement: true,
};

const uninitializedServerSnapshot = createUninitializedConsentState();

// Matches what getConsentState returns with no document and no events, so the
// SSG/hydration render output is unchanged.
export function getServerConsentSnapshot(): ConsentState {
  return isCookiebarEnabled() ? uninitializedServerSnapshot : disabledServerSnapshot;
}

function notifyAll() {
  const state = getConsentState();
  listeners.forEach((l) => {
    try {
      l(state);
    } catch {
      // no-op
    }
  });
}

function installHandlers() {
  if (handlersInstalled || typeof window === "undefined") return;
  handlersInstalled = true;

  // Helper to parse category consent from event detail
  const parseCategories = (categories: Record<string, unknown>): Omit<ConsentState, "initialized" | "necessary"> => {
    return {
      functional: categories.functional === true,
      analytics: categories.analytics === true,
      advertisement: categories.advertisement === true,
    };
  };

  // cookieyes_banner_load: Fires when banner loads, provides initial state
  try {
    document.addEventListener("cookieyes_banner_load", (event: Event) => {
      const customEvent = event as CustomEvent<CookieYesBannerLoadDetail>;
      const detail = customEvent.detail;

      if (detail?.categories) {
        latestEventState = {
          initialized: detail.isUserActionCompleted === true,
          necessary: true, // Always true
          ...parseCategories(detail.categories),
        };
        latestEventCookieFingerprint = readSharedCookieYesSnapshot()?.fingerprint;
        notifyAll();
      }
    });
  } catch {
    // Ignore if addEventListener fails
  }

  // cookieyes_consent_update: Fires when user interacts with banner
  try {
    document.addEventListener("cookieyes_consent_update", (event: Event) => {
      const customEvent = event as CustomEvent<CookieYesConsentUpdateDetail>;
      const detail = customEvent.detail;

      const accepted = Array.isArray(detail?.accepted)
        ? detail.accepted.filter((category): category is string => typeof category === "string")
        : undefined;
      const rejected = Array.isArray(detail?.rejected)
        ? detail.rejected.filter((category): category is string => typeof category === "string")
        : undefined;

      if (accepted || rejected) {
        latestEventState = {
          initialized: true,
          necessary: true, // Always true
          functional: accepted?.includes("functional") ?? false,
          analytics: accepted?.includes("analytics") ?? false,
          advertisement: accepted?.includes("advertisement") ?? false,
        };
        latestEventCookieFingerprint = readSharedCookieYesSnapshot()?.fingerprint;
        notifyAll();
      }
    });
  } catch {
    // Ignore if addEventListener fails
  }

  // CookieYes DOM events are tab-local, while its cookies are shared. Reconcile when this tab
  // becomes active so a grant cached here cannot outlive a withdrawal made elsewhere.
  try {
    window.addEventListener("focus", reconcileConsentFromSharedCookie);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") reconcileConsentFromSharedCookie();
    });
  } catch {
    // Ignore if browser event subscription is unavailable.
  }
}

export function subscribeConsent(listener: Listener): () => void {
  installHandlers();
  listeners.add(listener);

  // If consent is already available, notify on next tick
  if (typeof window !== "undefined") {
    const state = getConsentState();
    if (state.initialized) {
      // Microtask to ensure consistent ordering
      queueMicrotask(() => listener(state));
    }
  }

  return () => listeners.delete(listener);
}

// --- CookieYes helpers ---
function readCookieValue(name: string, cookie: string): string | null {
  const cookies = cookie ? cookie.split(/;\s*/) : [];
  for (const part of cookies) {
    const idx = part.indexOf("=");
    const key = tryDecodeURIComponent(idx > -1 ? part.slice(0, idx) : part);
    if (key === name) {
      const val = idx > -1 ? part.slice(idx + 1) : "";
      return tryDecodeURIComponent(val);
    }
  }
  return null;
}

type SharedCookieYesSnapshot = {
  fingerprint: string;
  state: ConsentState | null;
};

function readSharedCookieYesSnapshot(): SharedCookieYesSnapshot | undefined {
  if (typeof document === "undefined") return undefined;
  // readDocumentCookies already absorbs any cookie-access exception and returns "" when browser
  // privacy controls block the read. A blocked read therefore surfaces here as an empty cookie
  // string: the fingerprint becomes "" and state null, and getConsentState's fingerprint-change
  // path replaces stale tab-local event state with uninitialized (fail-closed) rather than an
  // exception ever reaching this function.
  const cookie = readDocumentCookies();
  const fingerprint = cookie
    .split(/;\s*/)
    .filter(Boolean)
    .filter((part) => {
      const separatorIndex = part.indexOf("=");
      const key = tryDecodeURIComponent(separatorIndex > -1 ? part.slice(0, separatorIndex) : part);
      return COOKIEYES_STATE_COOKIE_NAMES.has(key);
    })
    .sort()
    .join(";");
  return { fingerprint, state: readCookieYesConsent(cookie) };
}

function readCookieYesConsent(cookie: string): ConsentState | null {
  // Current format: the single composite `cookieyes-consent` cookie
  // (`consentid:…,consent:yes,action:yes,necessary:yes,functional:no,analytics:yes,…` —
  // matches @cookieyes/core's serializeCookie). `action:yes` ⇔ the user answered the banner.
  const composite = parseCompositeCookieYesValue(readCookieValue("cookieyes-consent", cookie));
  if (composite) {
    return composite;
  }

  // Legacy fallback: global `cky-consent` + per-category cookies. Detect presence
  const ckyConsent = readCookieValue("cky-consent", cookie);
  const cookieyesConsent = readCookieValue("cookieyes-consent", cookie);
  const anyCategory = [
    "cky-functional",
    "cky-performance",
    "cky-preferences",
    "cky-analytics",
    "cky-advertisement",
    "cky-marketing",
    "cookieyes-functional",
    "cookieyes-performance",
    "cookieyes-preferences",
    "cookieyes-analytics",
    "cookieyes-advertisement",
    "cookieyes-marketing",
  ].some((n) => readCookieValue(n, cookie));
  const initialized = Boolean(ckyConsent || cookieyesConsent || anyCategory);

  if (!initialized) {
    return null;
  }

  // If global consent indicates accepted, grant all non-necessary
  if (ckyConsent && /^(yes|granted|accept)/i.test(ckyConsent)) {
    return {
      initialized: true,
      necessary: true,
      functional: true,
      analytics: true,
      advertisement: true,
    };
  }

  // Read standard per-category cookies (CookieYes standard format)
  const yes = (names: string[]): boolean =>
    names.some((name) => /^(1|true|yes)$/i.test(readCookieValue(name, cookie) ?? ""));
  const functional = yes(["cky-functional", "cookieyes-functional"]);
  const analytics = yes(["cky-analytics", "cookieyes-analytics"]);
  const advertisement = yes(["cky-advertisement", "cookieyes-advertisement"]);

  return {
    initialized: true,
    necessary: true,
    functional,
    analytics,
    advertisement,
  };
}

const COMPOSITE_CATEGORY_KEYS = ["necessary", "functional", "analytics", "performance", "advertisement"];

// Parses the composite `key:value,key:value,…` cookie value. Returns null when the value
// doesn't carry the composite shape (absent, or a legacy scalar like `yes`).
function parseCompositeCookieYesValue(value: string | null): ConsentState | null {
  if (!value || !value.includes(":")) {
    return null;
  }

  const record: Record<string, string> = {};
  for (const entry of value.split(",")) {
    const separatorIndex = entry.indexOf(":");
    if (separatorIndex === -1) {
      continue;
    }
    record[entry.slice(0, separatorIndex).trim()] = entry.slice(separatorIndex + 1).trim();
  }

  const hasCompositeShape = "action" in record || COMPOSITE_CATEGORY_KEYS.some((key) => key in record);
  if (!hasCompositeShape) {
    return null;
  }

  const yes = (key: string) => record[key] === "yes";

  return {
    initialized: yes("action"),
    necessary: true,
    functional: yes("functional"),
    analytics: yes("analytics"),
    advertisement: yes("advertisement"),
  };
}
